import { React, useState, useEffect, useCallback } from "react";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/pl";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTheme } from "@mui/material/styles/";
import {
	useEditTransactionMutation,
	useGetWalletValueOnDate,
} from "../../services/queryHooks";
import { CategoryDisplay } from "./CategorySelect/CategoryDisplay";
import { Loading } from "../../components/common/Loading";
import { useTranslation } from "react-i18next";
import { validateTextField } from "../../utils/validation";
import {
	validateTransactionDate,
	validateWalletBalance,
	validateLinkedWallet,
	validateRequiredFields,
} from "../../utils/transactionValidation";

const Field = ({
	label,
	value,
	onChange,
	error,
	helperText,
	type = "text",
	InputProps,
	...props
}) => (
	<FormControl fullWidth sx={{ mt: 2 }} size="small">
		<TextField
			label={label}
			value={value}
			onChange={onChange}
			error={!!error}
			helperText={error || helperText}
			type={type}
			fullWidth
			InputProps={InputProps}
			{...props}
		/>
	</FormControl>
);

export default function EditTransactionModal({
	isOpen,
	onClose,
	wallets,
	transaction,
}) {
	const theme = useTheme();
	const { t } = useTranslation();
	const userLanguage = localStorage.getItem("i18nextLng");

	const [form, setForm] = useState({
		title: transaction.description,
		selectedCategory: transaction.categoryId,
		selectedSubcategory: transaction.subcategoryId,
		selectedWallet: transaction.walletId,
		selectedType: transaction.type,
		selectedDate: dayjs(transaction.createdAt),
		markAsUnimportant: !transaction.includeInAnalysis,
		amount:
			transaction.amount < 0 ? -transaction.amount : transaction.amount,
	});

	const [errors, setErrors] = useState({
		title: "",
		date: "",
		amount: "",
		wallet: "",
		type: "",
		category: "",
	});

	const editTransactionMutation = useEditTransactionMutation();
	const linkedWalletId = transaction.investmentDebtPayoff;

	const {
		data: walletValueOnDateQuery,
		isPending: walletValueOnDateQueryPending,
	} = useGetWalletValueOnDate(
		form.selectedWallet,
		form.selectedDate?.toISOString(),
		isOpen
	);

	const {
		data: linkedWalletValueOnDate,
		isPending: linkedWalletValueOnDatePending,
	} = useGetWalletValueOnDate(
		linkedWalletId,
		form.selectedDate?.toISOString(),
		isOpen && Boolean(linkedWalletId)
	);
	const resetErrors = () =>
		setErrors({
			title: "",
			date: "",
			amount: "",
			wallet: "",
			type: "",
			category: "",
		});

	const resetFormState = useCallback(() => {
		if (!transaction) return;
		setForm({
			title: transaction.description,
			selectedCategory: transaction.categoryId,
			selectedSubcategory: transaction.subcategoryId,
			selectedWallet: transaction.walletId,
			selectedType: transaction.type,
			selectedDate: dayjs(transaction.createdAt),
			markAsUnimportant: !transaction.includeInAnalysis,
			amount:
				transaction.amount < 0
					? -transaction.amount
					: transaction.amount,
		});
		resetErrors();
	}, [transaction]);

	useEffect(() => {
		if (isOpen) {
			resetFormState();
		}
	}, [isOpen, resetFormState]);

	const minDate = dayjs("2000-01-01");
	const maxDate = dayjs();

	const handleChange = (field) => (e) => {
		setForm((prev) => ({ ...prev, [field]: e.target.value }));
	};

	const handleCheckboxChange = (field) => (e) => {
		setForm((prev) => ({ ...prev, [field]: e.target.checked }));
	};

	const handleBalanceChange = (e) => {
		let value = e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".");
		if (/^\d{0,12}(\.\d{0,2})?$/.test(value))
			setForm((prev) => ({ ...prev, amount: value }));
	};

	const handleDateChange = (newValue) =>
		setForm((prev) => ({ ...prev, selectedDate: newValue }));

	const hasChanged = () => {
		const originalAmount =
			transaction.type === "expense"
				? -transaction.amount
				: transaction.amount;
		const fields = {
			title: form.title.trim(),
			category: form.selectedCategory,
			subcategory: form.selectedSubcategory,
			wallet: form.selectedWallet,
			type: form.selectedType,
			date: form.selectedDate.toISOString(),
			markAsUnimportant: form.markAsUnimportant,
			amount: Number(form.amount),
		};
		const original = {
			title: transaction.description.trim(),
			category: transaction.categoryId,
			subcategory: transaction.subcategoryId,
			wallet: transaction.walletId,
			type: transaction.type,
			date: dayjs(transaction.createdAt).toISOString(),
			markAsUnimportant: !transaction.includeInAnalysis,
			amount: Math.abs(originalAmount),
		};
		return Object.keys(fields).some((key) => fields[key] !== original[key]);
	};

	const getWalletsFilteredAndSorted = () =>
		wallets
			.filter((w) => w.type === "cash")
			.sort((a, b) =>
				a.currency === transaction.currency
					? -1
					: b.currency === transaction.currency
						? 1
						: 0
			);

	const validateInputs = () => {
		resetErrors();
		let allErrors = {};

		const {
			title,
			selectedDate,
			selectedWallet,
			selectedType,
			selectedCategory,
			amount,
		} = form;
		const walletObj = wallets.find((w) => w._id === selectedWallet);

		const dateError = validateTransactionDate(
			selectedDate,
			walletObj?.createdAt,
			minDate,
			t
		);
		if (dateError) {
			allErrors.date = dateError;
		}

		const titleError = validateTextField(title, t, {
			fieldName: t("transactionTitle"),
		});
		if (titleError) {
			allErrors.title = titleError;
		}

		const requiredErrors = validateRequiredFields(
			{
				amount,
				wallet: selectedWallet,
				type: selectedType,
				category: selectedCategory,
			},
			t
		);
		allErrors = { ...allErrors, ...requiredErrors };

		if (selectedType === "expense" && walletObj) {
			const currentBalance = walletObj.balance - transaction.amount;
			const historicalBalance =
				walletValueOnDateQuery?.value - transaction.amount;

			const balanceError = validateWalletBalance(
				amount,
				currentBalance,
				historicalBalance,
				walletObj.currency,
				walletValueOnDateQueryPending,
				walletValueOnDateQuery?.error,
				t
			);

			if (balanceError) {
				allErrors.amount = balanceError;
			}
		}

		if (transaction.investmentDebtPayoff) {
			const linkedWallet = wallets.find(
				(w) => w._id === transaction.investmentDebtPayoff
			);
			const newAmount =
				selectedType === "expense" ? -amount : Number(amount);

			const linkedErrors = validateLinkedWallet(
				newAmount,
				transaction.amount,
				linkedWallet,
				linkedWalletValueOnDate?.value,
				selectedDate,
				dayjs(transaction.createdAt),
				linkedWalletValueOnDatePending,
				linkedWalletValueOnDate?.error,
				t
			);

			if (linkedErrors && Object.keys(linkedErrors).length > 0) {
				allErrors = { ...allErrors, ...linkedErrors };
			}
		}

		if (Object.keys(allErrors).length > 0) {
			setErrors(allErrors);
			return false;
		}

		return true;
	};

	const submitForm = (e) => {
		e.preventDefault();
		if (!validateInputs()) return;

		if (!hasChanged()) {
			handleClose();
			return;
		}

		editTransactionMutation.mutate({
			transactionId: transaction._id,
			includeInAnalysis: !form.markAsUnimportant,
			description: form.title,
			amount:
				form.selectedType === "expense"
					? -Number(form.amount)
					: Number(form.amount),
			currency: wallets.find((w) => w._id === form.selectedWallet)
				.currency,
			createdAt: form.selectedDate.toISOString(),
			walletId: form.selectedWallet,
			categoryId: form.selectedCategory,
			subcategoryId: form.selectedSubcategory,
			type: form.selectedType,
		});

		handleClose();
	};

	const handleClose = () => {
		editTransactionMutation.reset();
		onClose();
	};

	if (
		isOpen &&
		form.selectedDate &&
		((form.selectedWallet && walletValueOnDateQueryPending) ||
			(linkedWalletId && linkedWalletValueOnDatePending))
	)
		return <Loading />;

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleClose}
				sx={{ margin: "auto", padding: 0 }}
				disableEnforceFocus
			>
				<DialogTitle>{t("editCashTransaction")}</DialogTitle>
				<form
					noValidate
					onSubmit={async (event) => {
						submitForm(event);
					}}
				>
					<DialogContent sx={{ width: 500 }}>
						<FormControl fullWidth sx={{ mt: 2 }} size="small">
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale={userLanguage}
							>
								<DatePicker
									slotProps={{ textField: { size: "small" } }}
									value={form.selectedDate}
									onChange={handleDateChange}
									error={!!errors.date}
									minDate={minDate}
									maxDate={maxDate}
								/>
							</LocalizationProvider>
						</FormControl>
						{errors.date && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errors.date}
							</FormHelperText>
						)}

						<Field
							label={t("title")}
							value={form.title}
							onChange={handleChange("title")}
							error={errors.title}
						/>

						<FormControl fullWidth sx={{ mt: 2 }} size="small">
							<InputLabel>{t("wallet")}</InputLabel>
							<Select
								required
								value={form.selectedWallet}
								label={t("wallet")}
								onChange={handleChange("selectedWallet")}
								error={!!errors.wallet}
							>
								{getWalletsFilteredAndSorted().map((wallet) =>
									wallet.currency === transaction.currency ? (
										<MenuItem
											key={wallet._id}
											value={wallet._id}
										>
											{wallet.name}
										</MenuItem>
									) : (
										<Tooltip
											key={wallet._id}
											arrow
											placement="top"
											title={t(
												"transactionAndWalletCurrencyMustMatch"
											)}
										>
											<span>
												<MenuItem
													key={wallet._id}
													value={wallet._id}
													disabled={true}
												>
													{wallet.name}
												</MenuItem>
											</span>
										</Tooltip>
									)
								)}
							</Select>
						</FormControl>
						{errors.wallet && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errors.wallet}
							</FormHelperText>
						)}

						<FormControl
							fullWidth
							sx={{ mt: 2, flex: 1 }}
							size="small"
						>
							<InputLabel>{t("type")}</InputLabel>
							<Select
								required
								value={form.selectedType}
								label={t("type")}
								onChange={(e) => {
									handleChange("selectedType")(e);
									setForm((prev) => ({
										...prev,
										selectedCategory: "",
										selectedSubcategory: "",
									}));
								}}
								error={!!errors.type}
							>
								<MenuItem key="income" value="income">
									{t("income")}
								</MenuItem>
								<MenuItem key="expense" value="expense">
									{t("expense")}
								</MenuItem>
							</Select>
						</FormControl>
						{errors.type && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errors.type}
							</FormHelperText>
						)}

						{form.selectedType && (
							<CategoryDisplay
								transaction={transaction}
								selectedType={form.selectedType}
								setSelectedCategory={(val) =>
									setForm((prev) => ({
										...prev,
										selectedCategory: val,
									}))
								}
								setSelectedSubcategory={(val) =>
									setForm((prev) => ({
										...prev,
										selectedSubcategory: val,
									}))
								}
								selectedCategory={form.selectedCategory}
								selectedSubcategory={form.selectedSubcategory}
								isInModal
								isError={errors.category}
							/>
						)}
						{errors.category && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errors.category}
							</FormHelperText>
						)}

						{!transaction.investmentDebtPayoff && (
							<FormControlLabel
								sx={{ mt: 1, ml: 1, flex: 1 }}
								size="small"
								control={
									<Checkbox
										onChange={handleCheckboxChange(
											"markAsUnimportant"
										)}
									/>
								}
								label={t("markAsUnimportant")}
								checked={form.markAsUnimportant}
							/>
						)}

						<Field
							label={t("balance")}
							value={form.amount}
							onChange={handleBalanceChange}
							error={errors.amount}
							InputProps={{
								inputMode: "decimal",
								startAdornment: form.selectedType ===
									"expense" && (
									<InputAdornment position="start">
										-
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										{transaction.currency}
									</InputAdornment>
								),
							}}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose}>{t("cancel")}</Button>
						<Button type="submit" variant="contained">
							{t("save")}
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
